import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Search, Loader2, Check, X, FileText, UserPlus, AlertTriangle, User } from 'lucide-react';
import { useInstitution } from '../contexts/InstitutionContext';
import { usePermissao } from '../hooks/usePermissao';
import { useToast, useConfirm } from './Feedback';
import { turnoParaPeriodo } from '../utils/periodo';
import { Modal } from './ui';
import AlunoBadges from './AlunoBadges';
import { API_URL } from '../api/config';

// Componente central do sistema: a lista de chamada em si (usado pela tela "/",
// ver App.js -> <Chamada>). Pontos mais delicados deste arquivo:
//  - getNormalizedDays: um aluno pode não ter matrícula "normal" formatada, então
//    tenta várias fontes em ordem de prioridade antes de decidir em quais dias ele tem aula.
//  - handleTogglePresence: faz update otimista (muda a UI antes da API responder)
//    e reverte se a chamada falhar — dá a sensação de instantâneo mesmo em rede ruim.
//  - fetchPresencas roda em polling (a cada CONFIG.POLLING_INTERVAL) para refletir
//    marcações feitas por outro usuário/dispositivo quase em tempo real.

// Constantes para evitar Magic Numbers e facilitar manutenção
const CONFIG = {
  POLLING_INTERVAL: 15000,
  SEARCH_DEBOUNCE: 500,
  MIN_SEARCH_CHARS: 3,
  ID_ATIVIDADE_IGNORADA: 4, // Ex: Atividades de contraturno ou administrativas
};

// Funções de utilidade movidas para fora do componente para evitar warnings de dependência do React
// e melhorar a performance (não são recriadas a cada render)
const normalizeString = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/-feira/g, '')
    .trim();

// Data de hoje em YYYY-MM-DD no fuso LOCAL (mesmo c\u00e1lculo replicado em
// App.js/Grade.js/DailyReport.jsx) \u2014 usada s\u00f3 pra saber se `dataAtual` j\u00e1
// passou, pro aviso de "chamada n\u00e3o finalizada" abaixo.
const getTodayLocal = () => {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const date = new Date(d.getTime() - offset * 60 * 1000);
  return date.toISOString().split('T')[0];
};

// Tenta extrair o turno de vários possíveis campos retornados pelo backend
const extrairTurno = (aluno) => {
  const possiveis = [
    aluno.turno,
    aluno.turno_matricula,
    aluno.periodo,
    aluno.horario,
    aluno.horario_turno,
    aluno.turno_atividade,
  ];
  for (const valor of possiveis) {
    if (valor !== undefined && valor !== null && String(valor).trim() !== '') {
      return String(valor).trim();
    }
  }
  return '';
};

const getNormalizedDays = (aluno, matriculasGlobais) => {
  // 1. Prioridade absoluta para o novo campo unificado do Backend (já filtrado por status)
  if (aluno.dias_matriculados !== undefined && aluno.dias_matriculados !== null) {
    const valor = String(aluno.dias_matriculados);
    if (valor.trim() !== '') {
      return valor.split(',').map(normalizeString).filter(Boolean);
    }
  }

  // 2. Fallback para matrículas carregadas da API /api/matriculas/por-instituicao
  if (matriculasGlobais && Array.isArray(matriculasGlobais)) {
    const dias = matriculasGlobais
      .filter((m) => m.aluno_id === aluno.id && String(m.status || '').toLowerCase() === 'ativo')
      .map((m) => normalizeString(m.dia_semana))
      .filter(Boolean);
    if (dias.length > 0) return [...new Set(dias)];
  }

  // 3. Fallback para o array de matrículas dentro do objeto aluno
  if (aluno.matriculas && Array.isArray(aluno.matriculas)) {
    return aluno.matriculas
      .filter((m) => {
        const id = m.idatividades || m.id_atividade || m.idatividade;
        return id !== undefined && parseInt(id, 10) !== CONFIG.ID_ATIVIDADE_IGNORADA;
      })
      .map((m) => normalizeString(m.dia_semana || m.dia))
      .filter(Boolean);
  }

  // 4. Fallbacks legados
  const raw = aluno.dias_vinculados || aluno.dias_semana || '';
  return String(raw).split(',').map(normalizeString).filter(Boolean);
};

// Componente para os indicadores de dias da semana (Memoizado)
const DayBadges = React.memo(({ aluno, matriculas }) => {
  const diasSemana = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'];
  const iniciais = ['S', 'T', 'Q', 'Q', 'S'];
  const listaDiasNormalizada = getNormalizedDays(aluno, matriculas);

  return (
    <span className="flex gap-1">
      {diasSemana.map((dia, idx) => {
        const diaComparacao = normalizeString(dia);
        const isAtivo = listaDiasNormalizada.some((d) => d === diaComparacao);
        return (
          <span
            key={dia}
            title={dia}
            className={`w-4 h-4 text-[9px] font-semibold rounded flex items-center justify-center ${isAtivo ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600'}`}
          >
            {iniciais[idx]}
          </span>
        );
      })}
    </span>
  );
});

// Componente para a linha do aluno (Memoizado para evitar re-render de toda a lista ao marcar presença)
const StudentRow = React.memo(
  ({ aluno, matriculas, onToggle, isLoading, podeEditar, justificativas = [] }) => {
    const normalizedDays = getNormalizedDays(aluno, matriculas);
    const frequency =
      aluno.frequencia ||
      aluno.frequencia_semanal ||
      (normalizedDays.length > 0 ? normalizedDays.filter((x) => x).length : 0);

    const [showJustificativa, setShowJustificativa] = useState(false);
    const [justificativaSelecionada, setJustificativaSelecionada] = useState(
      aluno.observacao || '',
    );
    const [fotoAmpliada, setFotoAmpliada] = useState(false);

    useEffect(() => {
      setJustificativaSelecionada(aluno.observacao || '');
    }, [aluno.observacao]);

    const handleJustificativaChange = (e) => {
      const valor = e.target.value;
      if (valor === '__remover__') {
        onToggle(aluno, '__remover__');
        setShowJustificativa(false);
        setJustificativaSelecionada('');
      } else if (valor) {
        onToggle(aluno, valor);
        setShowJustificativa(false);
        setJustificativaSelecionada(valor);
      }
    };

    const statusColor =
      aluno.status === 'presente'
        ? 'bg-green-500 hover:bg-green-600'
        : aluno.status === 'ausente'
          ? 'bg-red-500 hover:bg-red-600'
          : aluno.status === 'justificado'
            ? 'bg-blue-500 hover:bg-blue-600'
            : 'bg-gray-400 hover:bg-gray-500';
    const statusLabel =
      aluno.status === 'presente'
        ? 'Presente'
        : aluno.status === 'ausente'
          ? 'Ausente'
          : aluno.status === 'justificado'
            ? 'Justificado'
            : 'Marcar';

    return (
      <div
        className={`flex flex-row items-center justify-between gap-3 px-2.5 py-1.5 rounded-xl border bg-white dark:bg-gray-900 transition-all ${aluno.isManual ? 'border-l-4 border-l-blue-500 border-gray-100 dark:border-gray-800' : 'border-gray-100 dark:border-gray-800'} ${isLoading ? 'opacity-60' : ''}`}
      >
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <button
            type="button"
            onClick={() => aluno.foto_url && setFotoAmpliada(true)}
            disabled={!aluno.foto_url}
            title={aluno.foto_url ? 'Ampliar foto' : undefined}
            className="w-9 h-9 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0 disabled:cursor-default"
          >
            {aluno.foto_url ? (
              <img src={aluno.foto_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <User className="w-4 h-4 text-gray-300 dark:text-gray-600" />
            )}
          </button>
          {aluno.foto_url && (
            <Modal
              isOpen={fotoAmpliada}
              onClose={() => setFotoAmpliada(false)}
              title={aluno.nome}
              maxWidth="md"
            >
              <img
                src={aluno.foto_url}
                alt=""
                className="w-full max-h-[75vh] object-contain rounded-2xl"
              />
            </Modal>
          )}
          {aluno.isManual && <UserPlus className="w-4 h-4 text-blue-500 flex-shrink-0" />}
          <div className="min-w-0 flex-1">
            <div
              className="text-sm font-semibold leading-tight text-gray-900 dark:text-gray-100 flex items-center flex-wrap gap-1.5"
              translate="no"
            >
              {aluno.nome}
              <AlunoBadges aluno={aluno} />
            </div>
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 mt-0.5 text-[11px] leading-tight">
              <span className="text-gray-500 dark:text-gray-400">
                {frequency} aulas semanais
              </span>
              {aluno.acompanhamento && (
                <span className="text-purple-600 dark:text-purple-400 inline-flex items-center gap-1">
                  <span className="w-3.5 h-3.5 rounded-full bg-purple-100 dark:bg-purple-950 flex items-center justify-center text-[8px]">
                    👤
                  </span>
                  {aluno.acompanhamento}
                </span>
              )}
              {(aluno.ponto || aluno.ponto_desembarque) && (
                <span className="text-sky-600 dark:text-sky-400 inline-flex items-center gap-1">
                  <span className="w-3.5 h-3.5 rounded-full bg-sky-100 dark:bg-sky-950 flex items-center justify-center text-[8px]">
                    📍
                  </span>
                  {aluno.ponto_desembarque && aluno.ponto && aluno.ponto_desembarque !== aluno.ponto
                    ? `Emb: ${aluno.ponto} • Desemb: ${aluno.ponto_desembarque}`
                    : (aluno.ponto || aluno.ponto_desembarque)}
                </span>
              )}
            </div>
            {aluno.cuidados_medicos && (
              <div
                title={aluno.cuidados_medicos}
                className="text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border border-amber-200/60 dark:border-amber-800/50 rounded-md px-1.5 py-0.5 mt-0.5 flex items-center gap-1 font-medium max-w-md"
              >
                <span className="flex-shrink-0">💡</span>
                <span className="truncate">Cuidados: {aluno.cuidados_medicos}</span>
              </div>
            )}
            {aluno.status === 'justificado' && aluno.observacao && (
              <div className="text-[11px] text-blue-600 dark:text-blue-400 mt-0.5 flex items-center gap-1 font-medium bg-blue-50 dark:bg-blue-950/40 px-1.5 py-0.5 rounded-md w-fit">
                <FileText className="w-3 h-3 flex-shrink-0" />
                <span>Justificativa: {aluno.observacao}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-1 w-[108px] flex-shrink-0">
          <DayBadges aluno={aluno} matriculas={matriculas} />
          <div className="flex items-center gap-1 w-full">
            <button
              onClick={() => onToggle(aluno)}
              disabled={isLoading || !podeEditar}
              title={statusLabel}
              className={`flex-1 inline-flex items-center justify-center h-8 rounded-lg text-white transition-all disabled:cursor-not-allowed disabled:opacity-60 ${statusColor}`}
            >
              {isLoading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : aluno.status === 'presente' ? (
                <Check className="w-3.5 h-3.5" />
              ) : aluno.status === 'ausente' ? (
                <X className="w-3.5 h-3.5" />
              ) : (
                <FileText className="w-3.5 h-3.5" />
              )}
            </button>
            {podeEditar && (
              <button
                onClick={() => setShowJustificativa(!showJustificativa)}
                disabled={isLoading}
                title="Adicionar justificativa"
                className={`w-8 h-8 rounded-lg text-white flex items-center justify-center transition-all flex-shrink-0 ${showJustificativa ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-400 hover:bg-gray-500'}`}
              >
                <FileText className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {showJustificativa && (
            <select
              value={justificativaSelecionada}
              onChange={handleJustificativaChange}
              autoFocus
              onBlur={() => {
                if (!justificativaSelecionada) setShowJustificativa(false);
              }}
              className="h-10 px-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 min-w-[180px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            >
              <option value="">Selecione o motivo...</option>
              {justificativas.map((j) => (
                <option key={j} value={j}>
                  {j}
                </option>
              ))}
              {aluno.status === 'justificado' && (
                <option value="__remover__">❌ Remover justificativa</option>
              )}
            </select>
          )}
        </div>
      </div>
    );
  },
);

const AttendanceList = ({
  dataAtual,
  turno,
  transporte,
  professor,
  onAlunosLoaded,
  hojeServidor,
  justificativasVersion,
}) => {
  const { podeCriar, podeEditar } = usePermissao('/');
  const [alunos, setAlunos] = useState([]);
  const [matriculas, setMatriculas] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingStates, setLoadingStates] = useState({}); // Mapeia id_aluno -> boolean
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [manualAlunos, setManualAlunos] = useState([]);
  const [finalizandoChamada, setFinalizandoChamada] = useState(false);
  const [justificativas, setJustificativas] = useState([]);
  const { institutionId } = useInstitution();
  const toast = useToast();
  const confirm = useConfirm();
  const allAlunosRef = useRef([]);

  // Carrega matrículas da instituição para usar nos indicadores de dias
  useEffect(() => {
    if (!institutionId) return;

    axios
      .get(`${API_URL}/api/matriculas/por-instituicao`, {
        params: { status: 'ativo' },
        headers: { 'x-institution-id': institutionId, 'Cache-Control': 'no-cache' },
      })
      .then((res) => {
        const raw = res.data?.data ?? res.data;
        const dados = Array.isArray(raw) ? raw : [];
        setMatriculas(dados);
      })
      .catch((err) => console.error('Erro ao carregar matrículas:', err));
  }, [institutionId]);

  // Lista de motivos de falta desta instituição (ver
  // backend/justificativas-falta.js e JustificativasFaltaModal) — era um
  // array fixo dentro de StudentRow; agora é buscada aqui (uma vez pra lista
  // inteira, não por aluno) e passada como prop. `justificativasVersion`
  // (ver Chamada em App.js) força recarregar depois de criar/editar/apagar
  // uma justificativa no modal de gerenciamento, sem precisar recarregar a
  // página inteira.
  useEffect(() => {
    if (!institutionId) return;
    axios
      .get(`${API_URL}/api/justificativas-falta`, {
        headers: { 'x-institution-id': institutionId, 'Cache-Control': 'no-cache' },
      })
      .then((res) => {
        const raw = res.data;
        const dados = Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : [];
        setJustificativas(dados.map((j) => j.texto));
      })
      .catch((err) => console.error('Erro ao carregar justificativas de falta:', err));
  }, [institutionId, justificativasVersion]);

  // Usar Ref para onAlunosLoaded evita que fetchPresencas mude caso o pai re-renderize sem useCallback
  const onAlunosLoadedRef = useRef(onAlunosLoaded);
  useEffect(() => {
    onAlunosLoadedRef.current = onAlunosLoaded;
  }, [onAlunosLoaded]);

  // Ref pra fetchPresencas (polling) enxergar o loadingStates mais recente sem
  // precisar dele nas dependências do useCallback — isso recriaria a função
  // (e reiniciaria o setInterval do polling) a cada clique.
  const loadingStatesRef = useRef({});
  useEffect(() => {
    loadingStatesRef.current = loadingStates;
  }, [loadingStates]);

  // 1. Busca de dados com Cache Busting (Foco em Safari/iPhone)
  const fetchPresencas = useCallback(
    async (isSilent = false) => {
      if (!institutionId || !turno) {
        if (!isSilent) {
          setAlunos([]);
          setLoadingData(false);
          if (onAlunosLoadedRef.current) onAlunosLoadedRef.current([]);
        }
        return;
      }

      // Só ativa o loading visual se não for uma atualização silenciosa (polling)
      if (!isSilent) setLoadingData(true);

      try {
        const timestamp = Date.now();
        const [resAlunos, resPresencas] = await Promise.all([
          axios.get(`${API_URL}/api/alunos/por-dia`, {
            params: { data: dataAtual, turno, ...(professor ? { professor } : {}), t: timestamp },
            headers: { 'x-institution-id': institutionId, 'Cache-Control': 'no-cache' },
          }),
          axios.get(`${API_URL}/api/presenca`, {
            params: { data: dataAtual, t: timestamp },
            headers: { 'x-institution-id': institutionId, 'Cache-Control': 'no-cache' },
          }),
        ]);

        const rawAlunos = resAlunos.data?.data ?? resAlunos.data;
        const alunosData = Array.isArray(rawAlunos) ? rawAlunos : [];
        const rawPresencas = resPresencas.data?.data ?? resPresencas.data;
        const presencasData = Array.isArray(rawPresencas) ? rawPresencas : [];

        // Criamos um mapa de presença para o dia selecionado — só do PERÍODO
        // sendo exibido agora (ver turnoParaPeriodo). Sem esse filtro, um aluno
        // com matrícula dupla (turma do dia + ensaio à noite) que já tenha
        // presença registrada nos dois períodos pegaria o status errado aqui
        // (o registro de presença de um período "vazando" pro outro — era
        // exatamente o bug original).
        //
        // Registro SEM período (de antes dessa coluna existir, ver migrate-
        // add-periodo-presenca.js) também conta, como fallback — mas só se não
        // existir um registro com período batendo EXATO pro mesmo aluno, e
        // NUNCA quando o período atual é "noite" (a chamada da noite nem
        // existia como opção separada antes dessa coluna existir — um registro
        // antigo sem período, por definição, só pode ter vindo de manhã/tarde;
        // sem essa restrição, um aluno com presença antiga de outro turno
        // aparecia "presente" na noite mesmo sem a chamada da noite ter sido
        // feita ainda). Sem o fallback em manhã/tarde, presença antiga "sumia"
        // da tela. Funciona independente da ORDEM dos registros no array: o
        // match exato nunca é sobrescrito por um fallback processado depois dele.
        const periodoAtual = turnoParaPeriodo(turno);
        const statusMap = {};
        const observacaoMap = {};
        const temMatchExato = {};
        presencasData.forEach((registro) => {
          const regData = registro.data?.split('T')[0];
          if (regData !== dataAtual) return;
          if (registro.periodo === periodoAtual) {
            statusMap[registro.aluno_id] = registro.status;
            observacaoMap[registro.aluno_id] = registro.observacao;
            temMatchExato[registro.aluno_id] = true;
          } else if (
            registro.periodo == null &&
            periodoAtual !== 'noite' &&
            !temMatchExato[registro.aluno_id]
          ) {
            statusMap[registro.aluno_id] = registro.status;
            observacaoMap[registro.aluno_id] = registro.observacao;
          }
        });

        // Mesclamos o status de presença (presente/ausente/justificado) e observação na lista de alunos.
        // `aluno.status` aqui (em alunosData, vindo de /api/alunos/por-dia) é o
        // status DA CONTA do aluno (ex.: "ativo"), não de presença — nunca deve
        // ser usado como fallback, senão um aluno sem registro de presença
        // ainda acaba renderizando com um valor que não bate com
        // presente/ausente/justificado (efeito colateral: virava "Marcar"
        // cinza por engano quando o polling batia antes da confirmação do clique).
        const alunosMesclados = alunosData.map((aluno) => {
          const alunoCompleto =
            allAlunosRef.current.find((a) => String(a.id) === String(aluno.id)) || {};
          return {
            ...alunoCompleto,
            ...aluno,
            turno: extrairTurno(aluno) || aluno.turno,
            status: statusMap[aluno.id] ?? null,
            observacao: observacaoMap[aluno.id] ?? null,
          };
        });

        // Preserva o status e observação de qualquer aluno com uma marcação em andamento
        // (loadingStatesRef) — sem isso, o polling de 15s podia sobrescrever a
        // linha com o estado anterior à confirmação, ainda não visível no
        // snapshot que acabou de ser buscado.
        setAlunos((prevAlunos) => {
          const statusAtualPorId = new Map(prevAlunos.map((a) => [String(a.id), a.status]));
          const obsAtualPorId = new Map(prevAlunos.map((a) => [String(a.id), a.observacao]));
          return alunosMesclados.map((a) =>
            loadingStatesRef.current[a.id]
              ? {
                  ...a,
                  status: statusAtualPorId.get(String(a.id)) ?? a.status,
                  observacao: obsAtualPorId.get(String(a.id)) ?? a.observacao,
                }
              : a,
          );
        });

        if (onAlunosLoadedRef.current) {
          onAlunosLoadedRef.current(alunosMesclados);
        }
      } catch (error) {
        console.error('Erro ao carregar presenças:', error);
      } finally {
        setLoadingData(false);
      }
    },
    [institutionId, dataAtual, turno, professor],
  );

  useEffect(() => {
    fetchPresencas(false); // Carga inicial visível
    // Adiciona polling para manter a lista atualizada caso outro usuário marque presença
    const pollInterval = setInterval(() => fetchPresencas(true), CONFIG.POLLING_INTERVAL);
    return () => clearInterval(pollInterval);
  }, [fetchPresencas]);

  const [allAlunos, setAllAlunos] = useState([]);

  // 2. Carrega todos os alunos para busca manual
  useEffect(() => {
    if (!institutionId) return;

    axios
      .get(`${API_URL}/api/alunos`, { headers: { 'x-institution-id': institutionId } })
      .then((res) => {
        const raw = res.data?.data ?? res.data;
        const dados = Array.isArray(raw) ? raw : [];
        const dadosNormalizados = dados.map((a) => ({ ...a, turno: extrairTurno(a) || a.turno }));
        allAlunosRef.current = dadosNormalizados;
        setAllAlunos(dadosNormalizados);
      })
      .catch((err) => console.error('Erro ao carregar lista completa de alunos:', err));
  }, [institutionId]);

  // 2.1. Caso /por-dia venha sem acompanhamento/ponto, completa com os dados de /api/alunos
  useEffect(() => {
    if (!alunos.length || !allAlunos.length) return;

    const allMap = new Map(allAlunos.map((a) => [String(a.id), a]));
    let changed = false;

    const merged = alunos.map((a) => {
      const full = allMap.get(String(a.id));
      if (!full) return a;

      const updates = {};
      if (!a.acompanhamento && full.acompanhamento) updates.acompanhamento = full.acompanhamento;
      if (!a.ponto && full.ponto) updates.ponto = full.ponto;
      if (!a.ponto_desembarque && full.ponto_desembarque) updates.ponto_desembarque = full.ponto_desembarque;

      if (Object.keys(updates).length > 0) {
        changed = true;
        return { ...a, ...updates };
      }
      return a;
    });

    if (changed) setAlunos(merged);
  }, [alunos, allAlunos]);

  // Lógica de Busca Instantânea Local
  useEffect(() => {
    if (searchTerm.length >= CONFIG.MIN_SEARCH_CHARS) {
      const term = normalizeString(searchTerm);
      const results = allAlunos.filter((a) => normalizeString(a.nome).includes(term));
      setSearchResults(results.slice(0, 15)); // Limita a 15 para melhor performance UI
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, allAlunos]);

  // Mesmo critério que `alunosExibidos` usa mais abaixo pra decidir quem
  // aparece de verdade na tela — extraído aqui pra `jaVisivel` (dentro de
  // addManualStudent) nunca mais divergir do que a tela realmente mostra.
  // Bug corrigido nesta sessão: sem filtro de transporte selecionado (o caso
  // mais comum), a comparação antiga (`alunoTransporte === transporte`, sendo
  // `transporte` uma string vazia) nunca batia — todo clique na busca virava
  // "adição manual" (log + notificação + entrada no relatório mensal) mesmo
  // pro aluno já visível bem na lista. Confirmado com dado real: 112 das 333
  // adições manuais registradas na instituição 1 tinham turno E transporte
  // batendo certinho (motivo "indefinido") — puro falso-positivo desse bug.
  const alunoVisivelAgora = useCallback(
    (a) => {
      const transpAlunoRaw =
        a.transporte && a.transporte.trim() ? a.transporte : 'Sem transporte definido';
      const alunoTransporte = normalizeString(transpAlunoRaw);
      const filtroTransporte = normalizeString(transporte);
      const matchTransporte =
        !transporte || !transporte.trim() || alunoTransporte === filtroTransporte;

      const diasMapa = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];
      const diaDaSemanaHoje = normalizeString(diasMapa[new Date(dataAtual + 'T12:00:00').getDay()]);
      const grade = getNormalizedDays(a);
      const temAulaHoje = grade.length === 0 || grade.includes(diaDaSemanaHoje);
      const jaMarcado = a.status === 'presente' || a.status === 'ausente' || a.status === 'justificado';
      const deveMostrar = temAulaHoje || jaMarcado || grade.length === 0;

      return matchTransporte && deveMostrar;
    },
    [transporte, dataAtual],
  );

  const addManualStudent = (aluno) => {
    // 1. Verifica se já foi adicionado manualmente
    if (manualAlunos.some((a) => a.id === aluno.id)) {
      toast.warning('Este inscrito já foi adicionado manualmente.');
      return;
    }

    // 2. Verifica se o aluno já está visível na lista filtrada (turno já vem
    // filtrado direto do backend, ver `turno` em GET /api/alunos/por-dia —
    // comparar `a.turno`, o atributo FIXO do aluno, quebraria pra quem tem
    // matrícula dupla dia+noite, ex.: turno cadastrado "Manhã" mas a chamada
    // atual sendo a de "Noite").
    const jaVisivel = alunos.some((a) => a.id === aluno.id && alunoVisivelAgora(a));

    if (!jaVisivel) {
      // Verifica se o aluno tem aula no dia selecionado para orientar o usuário
      const diasMapa = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];
      const diaDaSemanaHoje = normalizeString(diasMapa[new Date(dataAtual + 'T12:00:00').getDay()]);
      const grade = getNormalizedDays(aluno, matriculas);
      const temAulaHoje = grade.length === 0 || grade.includes(diaDaSemanaHoje);

      if (!temAulaHoje && diaDaSemanaHoje) {
        const diaFormatado = diaDaSemanaHoje.charAt(0).toUpperCase() + diaDaSemanaHoje.slice(1);
        toast.info(
          `Aviso: "${aluno.nome}" não possui aula cadastrada para ${diaFormatado}s. Ele foi adicionado manualmente para registro.`,
          { autoClose: 5000 },
        );
      }

      // Busca o aluno na lista do dia para preservar o status e observação caso ele já tenha sido marcado
      const alunoExistente = alunos.find((a) => a.id === aluno.id);
      const novoAlunoManual = {
        ...(alunoExistente || aluno),
        status: alunoExistente ? alunoExistente.status : null,
        observacao: alunoExistente ? alunoExistente.observacao : null,
        isManual: true,
      };
      setManualAlunos((prev) => [novoAlunoManual, ...prev]);
      setSearchTerm('');
      setSearchResults([]);

      // Registra em auditoria/notificação: esse aluno não apareceu na lista
      // automática do turno/transporte/dia e precisou ser buscado manualmente
      // — indica possível matrícula/turno OU transporte cadastrado errado (o
      // backend compara os dois e aponta qual). Fire-and-forget: não deve
      // travar a UI nem impedir o professor de continuar a chamada se falhar.
      axios
        .post(
          `${API_URL}/api/presenca/adicao-manual`,
          {
            aluno_id: aluno.id,
            data: dataAtual,
            turno,
            transporte,
          },
          { headers: { 'x-institution-id': institutionId } },
        )
        .catch((err) => console.error('Erro ao registrar adição manual:', err));
    } else {
      toast.warning('Este inscrito já está visível na lista atual.');
    }
  };

  // Filtra os alunos localmente com base nos filtros do Header — mesmo
  // critério de `alunoVisivelAgora` acima (turno NÃO é refiltrado aqui:
  // `alunos` já vem filtrado pela matrícula do turno certo direto do backend,
  // ver `turno` em GET /api/alunos/por-dia — refiltrar por `a.turno`, o
  // atributo FIXO do aluno, excluiria por engano quem tem matrícula dupla
  // dia+noite, turno cadastrado "Manhã" mas presente na chamada de "Noite").
  const alunosExibidos = useMemo(() => {
    if (!alunos || !Array.isArray(alunos)) return [];

    const filtrados = alunos.filter(alunoVisivelAgora);

    // Mescla com os alunos adicionados manualmente que ainda não estão na lista filtrada
    const idsFiltrados = new Set(filtrados.map((a) => a.id));
    const manuaisUnicos = manualAlunos.filter((a) => !idsFiltrados.has(a.id));
    return [...manuaisUnicos, ...filtrados];
  }, [alunos, manualAlunos, alunoVisivelAgora]);

  // Aviso de "chamada não finalizada": só faz sentido pra um dia que já
  // passou (hoje ainda pode estar em andamento) — quantos inscritos esperados
  // continuam sem NENHUM registro (nem presente, nem ausente, nem
  // justificado). Não conta quem foi adicionado manualmente sem marcar nada
  // ainda como "pendente de finalizar" da MESMA forma que o resto — mas como
  // esses também não têm status, entram no mesmo cálculo naturalmente.
  // `hojeServidor` vem do App.js/Chamada (GET /api/hoje, hora do SERVIDOR);
  // getTodayLocal() só entra como chute enquanto essa resposta ainda não
  // chegou, pra não deixar o aviso indefinido logo no primeiro render.
  const chamadaPendente = dataAtual < (hojeServidor || getTodayLocal());
  const quantidadeSemRegistro = alunosExibidos.filter((a) => !a.status).length;
  // Dia que ainda não aconteceu: a lista aparece, mas nada pode ser marcado (o
  // backend também recusa). Protege contra aparelho com data errada.
  const dataFutura = dataAtual > (hojeServidor || getTodayLocal());
  const podeMarcar = podeEditar && !dataFutura;

  // Mensagem do backend para data futura; para os demais erros, a genérica.
  const mensagemDeErro = (error, generica) =>
    error.response?.data?.isDataFutura ? error.response.data.error : generica;

  // 2. Lógica de Toggle pessimista (só reflete na tela após confirmação do
  // servidor) com Bloqueio de Clique
  const handleTogglePresence = async (aluno, justificativa = null) => {
    // Bloqueio de interação (Debounce/Lock)
    if (loadingStates[aluno.id]) return;

    let novoStatus;
    let novaObservacao;

    if (justificativa) {
      if (justificativa === '__remover__') {
        novoStatus = null;
        novaObservacao = null;
      } else {
        // Se há justificativa, marca como justificado
        novoStatus = 'justificado';
        novaObservacao = justificativa;
      }
    } else {
      // Se o aluno já possui falta JUSTIFICADA, o clique no botão principal não deve
      // sobrescrever acidentalmente apagando a justificativa sem confirmação.
      if (aluno.status === 'justificado') {
        const motivoInfo = aluno.observacao ? `\nMotivo atual: "${aluno.observacao}"` : '';
        const acao = await confirm(
          `"${aluno.nome}" já possui falta justificada.${motivoInfo}\n\nO que deseja fazer?`,
          {
            title: 'Falta Justificada',
            confirmText: 'Mudar para Presente',
            cancelText: 'Manter Justificativa',
            extraText: '🗑️ Remover Justificativa',
            tone: 'warning',
          },
        );

        if (acao === 'extra') {
          novoStatus = null;
          novaObservacao = null;
        } else if (acao === true) {
          novoStatus = 'presente';
          novaObservacao = aluno.isManual
            ? 'Presença registrada via busca manual (fora do transporte/turno)'
            : null;
        } else {
          // Cancelar / manter
          return;
        }
      } else {
        // Senão, alterna entre presente e deletar (não marca como ausente)
        novoStatus = aluno.status === 'presente' ? null : 'presente';
        novaObservacao = aluno.isManual
          ? 'Presença registrada via busca manual (fora do transporte/turno)'
          : null;
      }
    }

    // 1. Garantir que o ID seja um número para evitar erros de tipo no backend (SQLite/Postgres)
    const alunoIdInt = parseInt(aluno.id, 10);
    if (isNaN(alunoIdInt)) return;

    // Identifica qual lista atualizar (alunos normais ou manuais)
    const updateList = aluno.isManual ? setManualAlunos : setAlunos;
    const periodoAtual = turnoParaPeriodo(turno);

    // Se o usuário está clicando para marcar PRESENÇA (onde na tela parecia não ter presença)
    if (!justificativa && novoStatus === 'presente') {
      try {
        setLoadingStates((prev) => ({ ...prev, [aluno.id]: true }));
        const checkRes = await axios.get(`${API_URL}/api/presenca/verificar`, {
          params: { aluno_id: alunoIdInt, data: dataAtual, periodo: periodoAtual },
          headers: { 'x-institution-id': institutionId, 'Cache-Control': 'no-cache' },
        });

        const jaPresenteNoBanco = checkRes.data?.data?.existe || checkRes.data?.existe;

        if (jaPresenteNoBanco) {
          setLoadingStates((prev) => ({ ...prev, [aluno.id]: false }));
          const labelTurno = turno || 'deste turno';
          const confirmarReverter = await confirm(
            `"${aluno.nome}" já possui presença no turno ${labelTurno}.\n\n` +
            `Deseja atualizar a chamada para carregar os dados mais recentes ou desmarcar este aluno?`,
            {
              title: 'Presença já registrada',
              confirmText: 'Desmarcar',
              cancelText: 'Manter presença',
              extraText: '🔄 Atualizar Chamada',
              tone: 'danger',
            },
          );

          if (confirmarReverter === 'extra') {
            // Recarrega a chamada inteira na hora sem fechar o app!
            toast.info('Atualizando chamada...');
            await fetchPresencas(false);
            toast.success('Chamada atualizada!');
            return;
          }

          if (!confirmarReverter) {
            // Cancela e atualiza localmente para mostrar que ele já estava presente
            updateList((prev) => prev.map((a) => (a.id === aluno.id ? { ...a, status: 'presente' } : a)));
            toast.info('Presença mantida.');
            return;
          }

          // Se escolheu reverter / desmarcar, muda o status para null
          novoStatus = null;
        }
      } catch (err) {
        console.error('Erro ao verificar duplicidade de presença no turno:', err);
      }
    }

    // --- ATUALIZAÇÃO PESSIMISTA ---
    // Não muda a cor do botão aqui — só liga o spinner (isLoading via
    // loadingStates). O status só é aplicado no sucesso do POST, lá embaixo.
    // Antes disso era otimista (mudava a cor na hora), o que fazia o botão
    // "piscar" verde e depois voltar pra cinza/vermelho quando o polling de
    // 15s (fetchPresencas) rodava antes do POST confirmar, numa rede lenta.
    setLoadingStates((prev) => ({ ...prev, [aluno.id]: true }));

    const payload = {
      data: dataAtual,
      periodo: periodoAtual,
      chamadas: [{ aluno_id: alunoIdInt, status: novoStatus, observacao: novaObservacao }],
    };

    try {
      await axios.post(`${API_URL}/api/presenca`, payload, {
        headers: { 'x-institution-id': institutionId },
      });
      // Sucesso confirmado pelo servidor: só agora aplica o novo status e observação.
      updateList((prev) =>
        prev.map((a) =>
          a.id === aluno.id
            ? { ...a, status: novoStatus, observacao: novaObservacao }
            : a,
        ),
      );
    } catch (error) {
      // Erro de rede/servidor: nada mudou visualmente (o status só seria
      // aplicado no sucesso, acima), então não há nada a reverter — só avisa.
      toast.error(
        mensagemDeErro(
          error,
          'Falha ao sincronizar presença. Verifique a conexão e tente novamente.',
        ),
      );
    } finally {
      // Libera o componente para novas interações
      setLoadingStates((prev) => ({ ...prev, [aluno.id]: false }));
    }
  };

  // Marca automaticamente como "ausente" todo aluno esperado que ainda não tem
  // registro na data (ver POST /api/presenca/finalizar em backend/presenca.js).
  // Não sobrescreve marcações já feitas.
  const handleFinalizarChamada = async () => {
    if (!institutionId || !dataAtual || !turno) {
      toast.warning('Selecione a data e o turno antes de finalizar a chamada.');
      return;
    }

    const ok = await confirm(
      'Deseja finalizar a chamada? Isso marcará automaticamente como ausente todos os inscritos esperados que ainda não tiverem registro.',
      { title: 'Finalizar chamada', confirmText: 'Finalizar', tone: 'primary' },
    );
    if (!ok) {
      return;
    }

    setFinalizandoChamada(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/presenca/finalizar`,
        { data: dataAtual, turno },
        { headers: { 'x-institution-id': institutionId } },
      );

      if (response.data && response.data.ausentes_registrados > 0) {
        toast.success(
          `Chamada finalizada! ${response.data.ausentes_registrados} ausências foram registradas automaticamente.`,
        );
      } else {
        toast.success('Chamada finalizada! Todos os inscritos já tinham registro.');
      }

      // Recarregar a lista para mostrar as ausências
      fetchPresencas(false);
    } catch (error) {
      console.error('Erro ao finalizar chamada:', error);
      toast.error(
        mensagemDeErro(error, 'Erro ao finalizar chamada. Verifique a conexão e tente novamente.'),
      );
    } finally {
      setFinalizandoChamada(false);
    }
  };

  return (
    <div className="space-y-2.5">
      {dataFutura && (
        <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-2xl border border-red-100 dark:border-red-900/40">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-400 flex-1">
            Esse dia ainda não aconteceu — não é possível fazer a chamada. Confira a data
            selecionada e a data do aparelho.
          </span>
        </div>
      )}
      {chamadaPendente && quantidadeSemRegistro > 0 && (
        <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-2xl border border-amber-100 dark:border-amber-900/40">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <span className="text-sm text-amber-700 dark:text-amber-400 flex-1">
            Chamada não finalizada — <strong>{quantidadeSemRegistro}</strong> inscrito(s) sem nenhum
            registro nesse dia.
          </span>
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        {podeCriar && !dataFutura && (
          <div className="relative flex-1">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Buscar inscrito por nome para adicionar à lista..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-9 pl-9 pr-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              />
            </div>
            {searchResults.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl shadow-lg max-h-64 overflow-auto">
                {searchResults.map((result) => (
                  <button
                    key={result.id}
                    onClick={() => addManualStudent(result)}
                    className="w-full text-left px-4 py-3 border-b border-gray-50 dark:border-gray-800 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <span
                      className="text-sm font-medium text-gray-900 dark:text-gray-100"
                      translate="no"
                    >
                      {result.nome}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                      ({result.turno} - {result.transporte})
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {podeMarcar && (
          <button
            onClick={handleFinalizarChamada}
            disabled={finalizandoChamada || !turno}
            className="inline-flex items-center justify-center gap-1.5 px-3.5 h-9 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {finalizandoChamada ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            <span>Finalizar Chamada</span>
          </button>
        )}
      </div>

      {loadingData ? (
        <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400 text-sm">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando lista...
        </div>
      ) : !turno ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400 text-sm">
          Selecione o turno para exibir os inscritos.
        </div>
      ) : alunosExibidos.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400 text-sm">
          Nenhum inscrito encontrado para os filtros selecionados.
        </div>
      ) : (
        <div className="space-y-1.5">
          {alunosExibidos.map((aluno) => (
            <StudentRow
              key={aluno.id}
              aluno={aluno}
              matriculas={matriculas}
              onToggle={handleTogglePresence}
              isLoading={loadingStates[aluno.id]}
              podeEditar={podeMarcar}
              justificativas={justificativas}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AttendanceList;
